import React, { useState } from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, Home, MapPin, CheckCircle, XCircle, Clock, Eye, FileText, Search, Image, Bed, Bath, Calendar, DollarSign, Shield, AlertTriangle, User, Phone, Mail } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { formatCurrency } from '../../utils/calculations';
import { Property, PropertyVerificationStatus } from '../../types';
type RiskBand = 'A' | 'B' | 'C' | 'D';
interface PropertyWithLandlord extends Property {
  landlordName: string;
  landlordEmail: string;
  landlordPhone: string;
  riskBand?: RiskBand;
}
// Mock data for properties pending verification
const MOCK_PROPERTIES: PropertyWithLandlord[] = [{
  id: 'prop1',
  landlordId: 'l1',
  landlordName: 'Tunde Bakare',
  landlordEmail: 'tunde@example.com',
  landlordPhone: '+234 803 456 7890',
  address: '12 Admiralty Way, Lekki Phase 1',
  city: 'Lagos',
  state: 'Lagos',
  propertyType: 'apartment',
  bedrooms: 3,
  bathrooms: 2,
  annualRent: 2400000,
  monthlyEquivalent: 200000,
  securityDeposit: 400000,
  leaseDuration: 12,
  availableFrom: '2026-02-01',
  photos: ['https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400', 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400', 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400'],
  description: 'Beautiful 3-bedroom apartment in the heart of Lekki Phase 1. Modern finishes, 24/7 security, and ample parking space. Close to major shopping centers and restaurants.',
  amenities: ['Parking', '24/7 Security', 'Generator', 'Air Conditioning', 'Balcony'],
  status: 'pending',
  verificationStatus: {
    status: 'pending',
    checks: {
      ownership: false,
      rentReasonableness: false,
      compliance: false,
      documentation: false
    }
  },
  ownershipDocs: ['title_deed.pdf'],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  riskBand: undefined
}, {
  id: 'prop2',
  landlordId: 'l2',
  landlordName: 'Amina Yusuf',
  landlordEmail: 'amina@example.com',
  landlordPhone: '+234 801 234 5678',
  address: '45 Gwarinpa Estate, Plot 234',
  city: 'Abuja',
  state: 'FCT',
  propertyType: 'house',
  bedrooms: 4,
  bathrooms: 3,
  annualRent: 3600000,
  monthlyEquivalent: 300000,
  securityDeposit: 600000,
  leaseDuration: 24,
  availableFrom: '2026-03-01',
  photos: ['https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400', 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400'],
  description: 'Spacious 4-bedroom detached house in serene Gwarinpa Estate. Large compound, boys quarters, and modern amenities throughout.',
  amenities: ['Parking', '24/7 Security', 'Generator', 'Swimming Pool', 'Garden', 'Furnished'],
  status: 'pending',
  verificationStatus: {
    status: 'under_review',
    checks: {
      ownership: true,
      rentReasonableness: false,
      compliance: false,
      documentation: true
    }
  },
  ownershipDocs: ['certificate_of_occupancy.pdf', 'survey_plan.pdf'],
  createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  riskBand: undefined
}, {
  id: 'prop3',
  landlordId: 'l3',
  landlordName: 'Chidi Okonkwo',
  landlordEmail: 'chidi@example.com',
  landlordPhone: '+234 805 678 9012',
  address: '8 Victoria Arobieke Street, Lekki',
  city: 'Lagos',
  state: 'Lagos',
  propertyType: 'studio',
  bedrooms: 1,
  bathrooms: 1,
  annualRent: 1200000,
  monthlyEquivalent: 100000,
  securityDeposit: 200000,
  leaseDuration: 6,
  availableFrom: '2026-02-15',
  photos: ['https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400'],
  description: 'Cozy studio apartment perfect for young professionals. Fully serviced with reliable power and water supply.',
  amenities: ['Parking', '24/7 Security', 'Generator', 'Air Conditioning', 'Elevator'],
  status: 'pending',
  verificationStatus: {
    status: 'pending',
    checks: {
      ownership: false,
      rentReasonableness: false,
      compliance: false,
      documentation: false
    }
  },
  ownershipDocs: ['purchase_agreement.pdf'],
  createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  updatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  riskBand: undefined
}, {
  id: 'prop4',
  landlordId: 'l1',
  landlordName: 'Tunde Bakare',
  landlordEmail: 'tunde@example.com',
  landlordPhone: '+234 803 456 7890',
  address: '22 Banana Island Road',
  city: 'Lagos',
  state: 'Lagos',
  propertyType: 'condo',
  bedrooms: 5,
  bathrooms: 5,
  annualRent: 12000000,
  monthlyEquivalent: 1000000,
  securityDeposit: 2000000,
  leaseDuration: 12,
  availableFrom: '2026-04-01',
  photos: ['https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400', 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400', 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400', 'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=400'],
  description: 'Luxury 5-bedroom penthouse on Banana Island. Premium finishes, private elevator, rooftop terrace with stunning views. Staff quarters included.',
  amenities: ['Parking', '24/7 Security', 'Generator', 'Swimming Pool', 'Gym', 'Air Conditioning', 'Furnished', 'Elevator', 'Garden'],
  status: 'verified',
  verificationStatus: {
    status: 'verified',
    reviewedBy: 'Admin',
    reviewedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    checks: {
      ownership: true,
      rentReasonableness: true,
      compliance: true,
      documentation: true
    }
  },
  ownershipDocs: ['title_deed.pdf', 'certificate_of_occupancy.pdf'],
  createdAt: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
  updatedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
  riskBand: 'A'
}];
const RISK_BAND_CONFIG = {
  A: {
    label: 'Premium',
    description: 'Premium properties, verified ownership, market-rate pricing',
    color: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700'
  },
  B: {
    label: 'Good',
    description: 'Good properties, standard verification, reasonable pricing',
    color: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700'
  },
  C: {
    label: 'Acceptable',
    description: 'Acceptable properties, minor concerns, above-market pricing',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700'
  },
  D: {
    label: 'High Risk',
    description: 'High-risk properties, documentation issues, pricing concerns',
    color: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700'
  }
};
export function PropertyVerification() {
  const {
    toast
  } = useToast();
  const [properties, setProperties] = useState(MOCK_PROPERTIES);
  const [selectedProperty, setSelectedProperty] = useState<PropertyWithLandlord | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [feedback, setFeedback] = useState('');
  const [selectedRiskBand, setSelectedRiskBand] = useState<RiskBand | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const filteredProperties = properties.filter(p => {
    const matchesStatus = statusFilter === 'all' || p.verificationStatus.status === statusFilter;
    const matchesSearch = p.address.toLowerCase().includes(searchQuery.toLowerCase()) || p.city.toLowerCase().includes(searchQuery.toLowerCase()) || p.landlordName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  const handleApprove = async () => {
    if (!selectedProperty) return;
    if (!selectedRiskBand) {
      toast.error('Please select a risk band before approving');
      return;
    }
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setProperties(prev => prev.map(p => p.id === selectedProperty.id ? {
      ...p,
      status: 'verified' as const,
      riskBand: selectedRiskBand,
      verificationStatus: {
        ...p.verificationStatus,
        status: 'verified' as const,
        reviewedBy: 'Admin',
        reviewedAt: new Date().toISOString(),
        feedback: reviewNotes || undefined,
        checks: {
          ownership: true,
          rentReasonableness: true,
          compliance: true,
          documentation: true
        }
      }
    } : p));
    setSelectedProperty(null);
    setReviewNotes('');
    setFeedback('');
    setSelectedRiskBand(null);
    setIsProcessing(false);
    toast.success('Property approved and published successfully!');
  };
  const handleReject = async () => {
    if (!selectedProperty) return;
    if (!feedback.trim()) {
      toast.error('Please provide feedback explaining the rejection');
      return;
    }
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setProperties(prev => prev.map(p => p.id === selectedProperty.id ? {
      ...p,
      status: 'rejected' as const,
      verificationStatus: {
        ...p.verificationStatus,
        status: 'rejected' as const,
        reviewedBy: 'Admin',
        reviewedAt: new Date().toISOString(),
        feedback: feedback
      }
    } : p));
    setSelectedProperty(null);
    setReviewNotes('');
    setFeedback('');
    setSelectedRiskBand(null);
    setIsProcessing(false);
    toast.success('Property rejected. Landlord has been notified.');
  };
  const getStatusBadge = (status: PropertyVerificationStatus['status']) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400',
      under_review: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400',
      verified: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400',
      rejected: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400',
      suspended: 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400'
    };
    const labels = {
      pending: 'Pending',
      under_review: 'Under Review',
      verified: 'Verified',
      rejected: 'Rejected',
      suspended: 'Suspended'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-bold border ${badges[status]}`} data-id="element-3166">
        {labels[status]}
      </span>;
  };
  const getRiskBandBadge = (band?: RiskBand) => {
    if (!band) return null;
    return <span className={`px-2 py-1 rounded-full text-xs font-bold border ${RISK_BAND_CONFIG[band].color}`} data-id="element-3167">
        Band {band}
      </span>;
  };
  const getPropertyTypeBadge = (type: Property['propertyType']) => {
    return <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium capitalize" data-id="element-3168">
        {type}
      </span>;
  };
  const pendingCount = properties.filter(p => p.verificationStatus.status === 'pending').length;
  const verifiedCount = properties.filter(p => p.verificationStatus.status === 'verified').length;
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 pb-16 lg:pb-0" data-id="element-3169">
      <Sidebar data-id="element-3170" />

      <main className="flex-1 lg:ml-[240px]" data-id="element-3171">
        <DashboardHeader data-id="element-3172" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3173">
          {/* Header */}
          <div className="mb-8" data-id="element-3174">
            <div className="flex items-center justify-between mb-4" data-id="element-3175">
              <div className="flex items-center space-x-3" data-id="element-3176">
                <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center" data-id="element-3177">
                  <Building className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-3178" />
                </div>
                <div data-id="element-3179">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3180">
                    Property Verification
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3181">
                    Review and approve property listings
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center space-x-6" data-id="element-3182">
                <div className="text-center" data-id="element-3183">
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400" data-id="element-3184">
                    {pendingCount}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3185">
                    Pending
                  </div>
                </div>
                <div className="text-center" data-id="element-3186">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400" data-id="element-3187">
                    {verifiedCount}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3188">
                    Verified
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-3189">
              <div className="relative flex-1" data-id="element-3190">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-3191" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by address, city, or landlord..." className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-3192" />
              </div>

              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-3193">
                <option value="all" data-id="element-3194">All Status</option>
                <option value="pending" data-id="element-3195">Pending</option>
                <option value="under_review" data-id="element-3196">Under Review</option>
                <option value="verified" data-id="element-3197">Verified</option>
                <option value="rejected" data-id="element-3198">Rejected</option>
              </select>
            </div>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3199">
            {/* Property List */}
            <div className="space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto pr-2" data-id="element-3200">
              {filteredProperties.length > 0 ? filteredProperties.map((property, index) => <motion.div key={property.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.1
            }} onClick={() => {
              setSelectedProperty(property);
              setSelectedRiskBand(property.riskBand || null);
              setReviewNotes('');
              setFeedback('');
            }} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-2 overflow-hidden cursor-pointer transition-all hover:shadow-md ${selectedProperty?.id === property.id ? 'border-blue-500 dark:border-blue-400' : 'border-gray-200 dark:border-gray-700'}`} data-id="element-3201">
                    <div className="flex" data-id="element-3202">
                      {/* Property Image */}
                      <div className="w-32 h-32 flex-shrink-0" data-id="element-3203">
                        <img src={property.photos[0] || 'https://via.placeholder.com/128'} alt={property.address} className="w-full h-full object-cover" data-id="element-3204" />
                      </div>

                      {/* Property Info */}
                      <div className="flex-1 p-4" data-id="element-3205">
                        <div className="flex items-start justify-between mb-2" data-id="element-3206">
                          <div className="flex-1 min-w-0" data-id="element-3207">
                            <h3 className="font-semibold text-gray-900 dark:text-white truncate" data-id="element-3208">
                              {property.address}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3209">
                              {property.city}, {property.state}
                            </p>
                          </div>
                          {getStatusBadge(property.verificationStatus.status)}
                        </div>

                        <div className="flex items-center space-x-3 text-xs text-gray-600 dark:text-gray-400 mb-2" data-id="element-3210">
                          {getPropertyTypeBadge(property.propertyType)}
                          <span className="flex items-center" data-id="element-3211">
                            <Bed className="h-3 w-3 mr-1" data-id="element-3212" />
                            {property.bedrooms}
                          </span>
                          <span className="flex items-center" data-id="element-3213">
                            <Bath className="h-3 w-3 mr-1" data-id="element-3214" />
                            {property.bathrooms}
                          </span>
                        </div>

                        <div className="flex items-center justify-between" data-id="element-3215">
                          <div data-id="element-3216">
                            <p className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-3217">
                              {formatCurrency(property.annualRent)}
                              <span className="text-xs font-normal text-gray-500" data-id="element-3218">
                                /year
                              </span>
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3219">
                              by {property.landlordName}
                            </p>
                          </div>
                          {property.riskBand && getRiskBandBadge(property.riskBand)}
                        </div>
                      </div>
                    </div>
                  </motion.div>) : <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center" data-id="element-3220">
                  <Building className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-3221" />
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-3222">
                    No properties found
                  </p>
                </div>}
            </div>

            {/* Detail Panel */}
            <div className="lg:sticky lg:top-24 lg:h-[calc(100vh-280px)]" data-id="element-3223">
              <AnimatePresence mode="wait" data-id="element-3224">
                {selectedProperty ? <motion.div key={selectedProperty.id} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 h-full overflow-y-auto" data-id="element-3225">
                    {/* Property Header */}
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700" data-id="element-3226">
                      <div className="flex items-start justify-between mb-4" data-id="element-3227">
                        <div data-id="element-3228">
                          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1" data-id="element-3229">
                            {selectedProperty.address}
                          </h2>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3230">
                            {selectedProperty.city}, {selectedProperty.state}
                          </p>
                        </div>
                        {getStatusBadge(selectedProperty.verificationStatus.status)}
                      </div>

                      {/* Landlord Info */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3" data-id="element-3231">
                        <div className="flex items-center space-x-3" data-id="element-3232">
                          <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold" data-id="element-3233">
                            {selectedProperty.landlordName.charAt(0)}
                          </div>
                          <div className="flex-1" data-id="element-3234">
                            <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-3235">
                              {selectedProperty.landlordName}
                            </p>
                            <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400" data-id="element-3236">
                              <span className="flex items-center" data-id="element-3237">
                                <Mail className="h-3 w-3 mr-1" data-id="element-3238" />
                                {selectedProperty.landlordEmail}
                              </span>
                              <span className="flex items-center" data-id="element-3239">
                                <Phone className="h-3 w-3 mr-1" data-id="element-3240" />
                                {selectedProperty.landlordPhone}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 space-y-6" data-id="element-3241">
                      {/* Photos Gallery */}
                      <div data-id="element-3242">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-3243">
                          <Image className="h-5 w-5 mr-2 text-blue-600" data-id="element-3244" />
                          Property Photos ({selectedProperty.photos.length})
                        </h3>
                        <div className="grid grid-cols-3 gap-2" data-id="element-3245">
                          {selectedProperty.photos.map((photo, index) => <div key={index} className="relative aspect-square rounded-lg overflow-hidden" data-id="element-3246">
                              <img src={photo} alt={`Property ${index + 1}`} className="w-full h-full object-cover" data-id="element-3247" />
                              <button className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100" data-id="element-3248">
                                <Eye className="h-6 w-6 text-white" data-id="element-3249" />
                              </button>
                            </div>)}
                        </div>
                      </div>

                      {/* Property Details */}
                      <div data-id="element-3250">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-3251">
                          <Home className="h-5 w-5 mr-2 text-blue-600" data-id="element-3252" />
                          Property Details
                        </h3>
                        <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-3253">
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3" data-id="element-3254">
                            <p className="text-gray-500 dark:text-gray-400 text-xs" data-id="element-3255">
                              Type
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white capitalize" data-id="element-3256">
                              {selectedProperty.propertyType}
                            </p>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3" data-id="element-3257">
                            <p className="text-gray-500 dark:text-gray-400 text-xs" data-id="element-3258">
                              Bedrooms
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white" data-id="element-3259">
                              {selectedProperty.bedrooms}
                            </p>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3" data-id="element-3260">
                            <p className="text-gray-500 dark:text-gray-400 text-xs" data-id="element-3261">
                              Bathrooms
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white" data-id="element-3262">
                              {selectedProperty.bathrooms}
                            </p>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3" data-id="element-3263">
                            <p className="text-gray-500 dark:text-gray-400 text-xs" data-id="element-3264">
                              Lease Duration
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white" data-id="element-3265">
                              {selectedProperty.leaseDuration} months
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Pricing */}
                      <div data-id="element-3266">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-3267">
                          <DollarSign className="h-5 w-5 mr-2 text-blue-600" data-id="element-3268" />
                          Pricing
                        </h3>
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4" data-id="element-3269">
                          <div className="grid grid-cols-3 gap-4 text-center" data-id="element-3270">
                            <div data-id="element-3271">
                              <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-3272">
                                Annual Rent
                              </p>
                              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-3273">
                                {formatCurrency(selectedProperty.annualRent)}
                              </p>
                            </div>
                            <div data-id="element-3274">
                              <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-3275">
                                Monthly
                              </p>
                              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-3276">
                                {formatCurrency(selectedProperty.monthlyEquivalent)}
                              </p>
                            </div>
                            <div data-id="element-3277">
                              <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-3278">
                                Deposit
                              </p>
                              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-3279">
                                {formatCurrency(selectedProperty.securityDeposit)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Amenities */}
                      {selectedProperty.amenities.length > 0 && <div data-id="element-3280">
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-3" data-id="element-3281">
                            Amenities
                          </h3>
                          <div className="flex flex-wrap gap-2" data-id="element-3282">
                            {selectedProperty.amenities.map((amenity, index) => <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full" data-id="element-3283">
                                  {amenity}
                                </span>)}
                          </div>
                        </div>}

                      {/* Ownership Documents */}
                      <div data-id="element-3284">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-3285">
                          <FileText className="h-5 w-5 mr-2 text-blue-600" data-id="element-3286" />
                          Ownership Documents
                        </h3>
                        <div className="space-y-2" data-id="element-3287">
                          {selectedProperty.ownershipDocs.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-3288">
                              <div className="flex items-center space-x-2" data-id="element-3289">
                                <FileText className="h-4 w-4 text-gray-400" data-id="element-3290" />
                                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3291">
                                  {doc.replace(/_/g, ' ').replace('.pdf', '')}
                                </span>
                              </div>
                              <button className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-3292">
                                <Eye className="h-3 w-3 mr-1" data-id="element-3293" />
                                View
                              </button>
                            </div>)}
                        </div>
                      </div>

                      {/* Risk Classification (only for pending/under_review) */}
                      {(selectedProperty.verificationStatus.status === 'pending' || selectedProperty.verificationStatus.status === 'under_review') && <div data-id="element-3294">
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-3295">
                            <Shield className="h-5 w-5 mr-2 text-blue-600" data-id="element-3296" />
                            Risk Classification
                          </h3>
                          <div className="space-y-3" data-id="element-3297">
                            {(['A', 'B', 'C', 'D'] as RiskBand[]).map(band => <label key={band} className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${selectedRiskBand === band ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`} data-id="element-3298">
                                  <input type="radio" name="riskBand" value={band} checked={selectedRiskBand === band} onChange={() => setSelectedRiskBand(band)} className="mt-1 mr-3" data-id="element-3299" />
                                  <div className="flex-1" data-id="element-3300">
                                    <div className="flex items-center space-x-2 mb-1" data-id="element-3301">
                                      <span className={`px-2 py-0.5 rounded text-xs font-bold border ${RISK_BAND_CONFIG[band].color}`} data-id="element-3302">
                                        Band {band}
                                      </span>
                                      <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3303">
                                        {RISK_BAND_CONFIG[band].label}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3304">
                                      {RISK_BAND_CONFIG[band].description}
                                    </p>
                                  </div>
                                </label>)}
                          </div>
                        </div>}

                      {/* Admin Actions */}
                      {(selectedProperty.verificationStatus.status === 'pending' || selectedProperty.verificationStatus.status === 'under_review') && <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3305">
                          <div data-id="element-3306">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3307">
                              Review Notes (Optional)
                            </label>
                            <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" placeholder="Add any notes about this property..." data-id="element-3308" />
                          </div>

                          <div data-id="element-3309">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3310">
                              Feedback (Required for rejection)
                            </label>
                            <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" placeholder="Explain any issues or rejection reasons..." data-id="element-3311" />
                          </div>

                          <div className="flex space-x-3" data-id="element-3312">
                            <button onClick={handleReject} disabled={isProcessing} className="flex-1 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors flex items-center justify-center disabled:opacity-50" data-id="element-3313">
                              {isProcessing ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" data-id="element-3314" /> : <>
                                  <XCircle className="h-5 w-5 mr-2" data-id="element-3315" />
                                  Reject
                                </>}
                            </button>
                            <button onClick={handleApprove} disabled={isProcessing} className="flex-1 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50" data-id="element-3316">
                              {isProcessing ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" data-id="element-3317" /> : <>
                                  <CheckCircle className="h-5 w-5 mr-2" data-id="element-3318" />
                                  Approve & Publish
                                </>}
                            </button>
                          </div>
                        </div>}

                      {/* Already Processed */}
                      {(selectedProperty.verificationStatus.status === 'verified' || selectedProperty.verificationStatus.status === 'rejected') && <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3319">
                          <div className={`p-4 rounded-lg ${selectedProperty.verificationStatus.status === 'verified' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`} data-id="element-3320">
                            <div className="flex items-center space-x-2 mb-2" data-id="element-3321">
                              {selectedProperty.verificationStatus.status === 'verified' ? <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" data-id="element-3322" /> : <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" data-id="element-3323" />}
                              <span className={`font-semibold ${selectedProperty.verificationStatus.status === 'verified' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`} data-id="element-3324">
                                {selectedProperty.verificationStatus.status === 'verified' ? 'Property Verified' : 'Property Rejected'}
                              </span>
                            </div>
                            {selectedProperty.verificationStatus.reviewedAt && <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3325">
                                Reviewed on{' '}
                                {new Date(selectedProperty.verificationStatus.reviewedAt).toLocaleDateString()}
                              </p>}
                            {selectedProperty.riskBand && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-3326">
                                Risk Band:{' '}
                                <span className="font-medium" data-id="element-3327">
                                  {selectedProperty.riskBand}
                                </span>
                              </p>}
                            {selectedProperty.verificationStatus.feedback && <p className="text-sm text-gray-600 dark:text-gray-400 mt-2" data-id="element-3328">
                                <strong data-id="element-3329">Feedback:</strong>{' '}
                                {selectedProperty.verificationStatus.feedback}
                              </p>}
                          </div>
                        </div>}
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center h-full flex flex-col items-center justify-center" data-id="element-3330">
                    <Building className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" data-id="element-3331" />
                    <p className="text-gray-500 dark:text-gray-400" data-id="element-3332">
                      Select a property to review
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>
    </div>;
}